import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { authAPI } from '../config/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '';

  // If the user landed here because the global axios interceptor caught an
  // expired JWT mid-checkout, show a clear "session expired" banner so they
  // don't think the site is broken.
  useEffect(() => {
    if (sessionStorage.getItem('session_expired_msg')) {
      setSessionExpired(true);
      sessionStorage.removeItem('session_expired_msg');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      // 1) Explicit returnUrl param wins.  2) Then the post-login redirect
      // saved by the 401 interceptor.  3) Otherwise fall back to dashboards.
      const fromExpiry = sessionStorage.getItem('post_login_redirect');
      if (fromExpiry) sessionStorage.removeItem('post_login_redirect');
      if (returnUrl) {
        navigate(returnUrl);
      } else if (fromExpiry && !fromExpiry.startsWith('/login')) {
        navigate(fromExpiry);
      } else if (res.data.user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="pt-28 min-h-screen flex items-center justify-center bg-[#F7F8FA]">
      <div className="w-full max-w-md mx-6">
        <div className="bg-white rounded-xl p-8 md:p-10 shadow-lg border border-gray-100">
          <h1 className="text-3xl text-center mb-2 text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Welcome <span className="text-[#F57C20] italic">Back</span>
          </h1>
          <p className="text-[#6B7280] text-sm text-center mb-8">Sign in to your account</p>

          {sessionExpired && (
            <div data-testid="session-expired-banner" className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 mb-4 rounded-lg flex items-start gap-2">
              <span className="text-amber-600 text-base leading-none mt-0.5">⏱</span>
              <span><strong>Session expired</strong> — please sign in again to continue your booking. We&apos;ll bring you right back to where you were.</span>
            </div>
          )}

          {error && <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 mb-6 rounded-lg">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email" className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" placeholder="your@email.com" />
            </div>
            <div>
              <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password" className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 pr-12 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" placeholder="Your password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="login-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F57C20] transition-colors">
                  {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-[#F57C20] text-white py-4 text-sm font-semibold tracking-wide rounded-lg hover:bg-[#E06A10] transition-all disabled:opacity-50">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-[#6B7280] text-sm text-center mt-6">
            Don&apos;t have an account?{' '}
            <Link to={`/register${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`} className="text-[#F57C20] font-semibold hover:underline">Register</Link>
          </p>
          <p className="text-[#9CA3AF] text-sm text-center mt-3">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="hover:text-[#F57C20] transition-colors">Forgot Password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
